import { useContext, useEffect } from 'react';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';

import { useFeatureFlags } from 'Utilities/hooks';
import { isOfficeHours } from 'Utilities/isOfficeHours/isOfficeHours';

import { getAgentTeamsAvailability } from 'Services/privateApi';

type AgentTeamsAvailabilityResponse = {
  data?: {
    profilingTeamAvailable?: boolean;
  };
};

const POLL_INTERVAL_MS = 180_000;

const isIdTokenFresh = (idToken?: string): boolean => {
  try {
    const base64 = (idToken?.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    // base64url strips '=' padding; atob() needs the length to be a multiple of 4.
    const { exp } = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export const useAgentAvailability = () => {
  const { useAgentAvailabilityPolling: pollingEnabled } = useFeatureFlags();
  const { configActions } = useContext(ConfigContext);
  const { userState } = useContext(UserContext);

  const isLoggedIn = Number.isInteger(userState?.id);
  const usesCognito = Boolean(userState?.cognitoTokens);
  const idToken = userState?.cognitoTokens?.idToken;

  useEffect(() => {
    if (!pollingEnabled) {
      return undefined;
    }
    // only poll while the token is valid
    const canFetch = () => isLoggedIn && (!usesCognito || isIdTokenFresh(idToken));

    if (!canFetch()) {
      return undefined;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    configActions.update({ agentAvailability: { liveChatAvailable: isOfficeHours() } });

    const stopPolling = () => {
      if (intervalId === undefined) {
        return;
      }
      clearInterval(intervalId);
      intervalId = undefined;
    };

    const fetchAvailability = () => {
      if (!canFetch()) {
        stopPolling();
        configActions.update({ agentAvailability: { liveChatAvailable: isOfficeHours() } });
        return;
      }

      getAgentTeamsAvailability()
        .then((response: AgentTeamsAvailabilityResponse) => {
          if (cancelled) {
            return;
          }
          const available = response?.data?.profilingTeamAvailable ?? isOfficeHours();
          configActions.update({ agentAvailability: { liveChatAvailable: available } });
        })
        .catch((error: Error) => {
          if (cancelled) {
            return;
          }
          configActions.update({ agentAvailability: { liveChatAvailable: isOfficeHours() } });
          window?.Sentry?.captureException?.(error, {
            level: 'warning',
            tags: { scope: 'useAgentAvailability' },
          });
        });
    };

    const startPolling = () => {
      if (intervalId !== undefined) {
        return;
      }
      intervalId = setInterval(fetchAvailability, POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchAvailability();
        startPolling();
      }
    };

    if (!document.hidden) {
      fetchAvailability();
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollingEnabled, idToken, isLoggedIn, usesCognito]); // eslint-disable-line react-hooks/exhaustive-deps
};
